export const COPYRIGHT_STATUS = {
    COPYRIGHT: 'copyright',
    SAFE: 'safe',
    UNCHECKED: 'unchecked',
};

export const normalizeBookTitle = (value = '') => String(value)
    .toLocaleLowerCase('vi-VN')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[’']/g, "'")
    .replace(/'s\b/g, 's')
    .replace(/&/g, ' and ')
    .replace(/([a-z])(\d{1,3})\b/g, '$1 $2')
    .replace(/\b(full\s*)?audio\s*book\b/g, ' ')
    .replace(/\bfull\s*version\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const getCopyrightAliases = record => [
    record?.title,
    ...(Array.isArray(record?.aliases) ? record.aliases : []),
].map(normalizeBookTitle).filter(Boolean);

const VOLUME_MARKERS = new Set([
    'book', 'books', 'bk', 'bks', 'volume', 'volumes', 'vol', 'vols',
    'part', 'parts', 'season', 'seasons', 'series', 'episode', 'episodes', 'ep',
    'chapter', 'chapters', 'chuong', 'phan', 'tap', 'quyen',
]);
const EDITION_MARKERS = new Set(['omnibus', 'collection', 'anthology', 'boxset', 'boxed']);
const NUMBER_WORDS = new Set([
    'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty',
]);

const isVolumeNumber = token => /^\d{1,3}(?:st|nd|rd|th)?$/.test(token)
    || /^(?:i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i.test(token)
    || NUMBER_WORDS.has(token);

const isDistinctiveSeriesKey = key => {
    const compactLength = key.replace(/\s/g, '').length;
    return compactLength >= (key.includes(' ') ? 5 : 6);
};

export const getCopyrightSeriesKey = value => {
    const normalized = normalizeBookTitle(value);
    if (!normalized) return '';
    const tokens = normalized.split(' ');
    let cutAt = tokens.length;

    for (let index = 1; index < tokens.length; index += 1) {
        const token = tokens[index];
        const previous = tokens[index - 1];
        if (VOLUME_MARKERS.has(token) && isVolumeNumber(tokens[index + 1])) {
            cutAt = index;
            break;
        }
        if (isVolumeNumber(token)) {
            cutAt = VOLUME_MARKERS.has(previous) ? index - 1 : index;
            break;
        }
    }

    const key = tokens.slice(0, cutAt).join(' ').trim();
    return isDistinctiveSeriesKey(key) ? key : '';
};

const isStructuredPrefixMatch = (left, right) => {
    const leftTokens = left.split(' ');
    const rightTokens = right.split(' ');
    const [shorter, longer] = leftTokens.length <= rightTokens.length
        ? [leftTokens, rightTokens]
        : [rightTokens, leftTokens];
    if (shorter.length >= longer.length || !shorter.every((token, index) => token === longer[index])) return false;
    const base = shorter.join(' ');
    if (!isDistinctiveSeriesKey(base)) return false;
    const suffixStart = longer[shorter.length];
    return isVolumeNumber(suffixStart) || VOLUME_MARKERS.has(suffixStart) || EDITION_MARKERS.has(suffixStart);
};

const isSeriesVariant = (left, right) => {
    const leftKey = getCopyrightSeriesKey(left);
    const rightKey = getCopyrightSeriesKey(right);
    return Boolean(leftKey && rightKey && leftKey === rightKey) || isStructuredPrefixMatch(left, right);
};

export const getCopyrightTitleVariants = value => {
    const raw = String(value || '').trim();
    const normalizedFull = normalizeBookTitle(raw);
    if (!normalizedFull) return [];
    const rawCandidates = new Set([raw]);
    const groupPatterns = [/\(([^()]*)\)/g, /\[([^\[\]]*)\]/g];

    groupPatterns.forEach(pattern => {
        let match;
        while ((match = pattern.exec(raw)) !== null) rawCandidates.add(match[1]);
        rawCandidates.add(raw.replace(pattern, ' '));
    });

    [...rawCandidates].forEach(candidate => {
        const dashPrefix = candidate.split(/\s+[-–—]\s*|\s*[-–—]\s+/)[0];
        const byPrefix = candidate.split(/\s+by\s*:?\s+/i)[0];
        const colonPrefix = candidate.split(':')[0];
        rawCandidates.add(dashPrefix);
        rawCandidates.add(byPrefix);
        rawCandidates.add(colonPrefix);
    });

    const variants = new Set([normalizedFull]);
    rawCandidates.forEach(candidate => {
        const normalized = normalizeBookTitle(candidate);
        if (normalized && isDistinctiveSeriesKey(normalized)) variants.add(normalized);
        const withoutEdition = normalized
            .replace(/\b(?:the )?complete series\b.*$/, '')
            .replace(/\bseries(?: set)?\b.*$/, '')
            .replace(/\b(?:complete )?(?:box set|boxset|boxed set)\b.*$/, '')
            .trim();
        if (withoutEdition && isDistinctiveSeriesKey(withoutEdition)) variants.add(withoutEdition);
    });
    return [...variants];
};

const strictRecordTitles = record => {
    const stored = [record?.normalizedTitle, ...(Array.isArray(record?.normalizedAliases) ? record.normalizedAliases : [])];
    return [...new Set([...stored, ...getCopyrightAliases(record)].map(normalizeBookTitle).filter(Boolean))];
};

const recordSearchTitles = record => {
    const values = [record?.title, ...(Array.isArray(record?.aliases) ? record.aliases : []),
        record?.normalizedTitle, ...(Array.isArray(record?.normalizedAliases) ? record.normalizedAliases : [])];
    return [...new Set(values.flatMap(getCopyrightTitleVariants))];
};

export const findCopyrightRecord = (title, records = []) => {
    const normalized = normalizeBookTitle(title);
    if (!normalized) return null;
    const strictExact = records.find(record => strictRecordTitles(record).includes(normalized));
    if (strictExact) return strictExact;

    const inputTitles = getCopyrightTitleVariants(title);
    const literalMatches = records.filter(record => {
        const candidates = recordSearchTitles(record);
        return inputTitles.some(input => candidates.includes(input));
    });
    if (literalMatches.length) {
        return literalMatches.find(record => record.status === COPYRIGHT_STATUS.COPYRIGHT) || literalMatches[0];
    }

    const seriesMatches = records.filter(record => {
        const candidates = recordSearchTitles(record);
        return inputTitles.some(input => candidates.some(candidate => isSeriesVariant(input, candidate)));
    });
    return seriesMatches.find(record => record.status === COPYRIGHT_STATUS.COPYRIGHT)
        || seriesMatches[0]
        || null;
};

export const getTaskCopyrightInfo = (task, records = []) => {
    const record = findCopyrightRecord(task?.bookTitle, records);
    if (record) return { status: record.status || COPYRIGHT_STATUS.COPYRIGHT, record, legacy: false };
    if (task?.isUnsafe) {
        return {
            status: COPYRIGHT_STATUS.COPYRIGHT,
            legacy: true,
            record: {
                id: `legacy:${normalizeBookTitle(task.bookTitle)}`,
                title: task.bookTitle,
                status: COPYRIGHT_STATUS.COPYRIGHT,
                reason: 'Task này từng được đánh dấu bản quyền theo dữ liệu cũ.',
                note: 'Hãy thêm cuốn này vào Kho bản quyền để đồng bộ cho mọi task cùng tên.',
                revisionKey: 'legacy',
            },
        };
    }
    return { status: COPYRIGHT_STATUS.UNCHECKED, record: null, legacy: false };
};

export const copyrightStatusLabel = status => status === COPYRIGHT_STATUS.COPYRIGHT
    ? 'Có bản quyền'
    : status === COPYRIGHT_STATUS.SAFE ? 'Đã kiểm tra an toàn' : 'Chưa kiểm tra';

// --- NAS archive matching (shared by the React app and api/nas.js) ---
const stripEditionTail = value => value
    .replace(/\b(?:the )?complete series\b.*$/, '')
    .replace(/\bseries(?: set)?\b.*$/, '')
    .replace(/\b(?:complete )?(?:box set|boxset|boxed set)\b.*$/, '')
    .trim();

// Keys safe enough to link a task to a NAS folder automatically: the full title plus the
// series name without "Book 1" / "Series" suffixes ("Blade's Rest Series Book 1" -> "blades rest").
export const getNasMatchKeys = value => {
    const normalized = normalizeBookTitle(value);
    if (!normalized) return [];
    const keys = new Set([normalized]);
    [normalized, getCopyrightSeriesKey(value)].forEach(base => {
        if (!base) return;
        [base, stripEditionTail(base)].forEach(key => {
            if (key && isDistinctiveSeriesKey(key)) keys.add(key);
        });
    });
    return [...keys];
};

const authorTokens = value => normalizeBookTitle(value).split(' ').filter(token => token.length > 2);

export const nasAuthorsMatch = (left, right) => {
    if (normalizeBookTitle(left) && normalizeBookTitle(left) === normalizeBookTitle(right)) return true;
    const a = authorTokens(left);
    const b = new Set(authorTokens(right));
    return a.some(token => b.has(token));
};

export const buildNasLookup = (items = []) => {
    const byKey = new Map();
    const byTask = new Map();
    items.forEach(item => {
        if (item.quarantined) return;
        (item.keys || []).forEach(key => {
            if (!byKey.has(key)) byKey.set(key, []);
            byKey.get(key).push(item);
        });
        (item.taskIds || []).forEach(id => byTask.set(id, item));
    });
    return { byKey, byTask };
};

// Returns the archive a task should download from. A match is "certain" when it is the only
// candidate, the shared key is distinctive (or the author confirms it) and the author does not conflict.
export const findNasMatch = (task, lookup) => {
    const empty = { item: null, certain: false, candidates: [] };
    if (!task || !lookup) return empty;
    const linked = task.id ? lookup.byTask.get(task.id) : null;
    if (linked) return { item: linked, certain: true, candidates: [linked] };
    const found = new Map();
    getNasMatchKeys(task.bookTitle).forEach(key => {
        (lookup.byKey.get(key) || []).forEach(item => {
            if ((item.excludeTaskIds || []).includes(task.id)) return;
            const entry = found.get(item.id) || { item, distinctive: false };
            entry.distinctive = entry.distinctive || isDistinctiveSeriesKey(key);
            found.set(item.id, entry);
        });
    });
    const candidates = [...found.values()];
    const accepted = candidates.filter(({ item, distinctive }) => {
        const hasAuthors = Boolean(task.author && item.author);
        if (hasAuthors) return nasAuthorsMatch(task.author, item.author);
        return distinctive;
    });
    return {
        item: accepted[0]?.item || null,
        certain: accepted.length === 1 && candidates.length === 1,
        candidates: candidates.map(entry => entry.item)
    };
};
